// Social Scheduler Service
// - Validates inputs before requests
// - Uses production API domain (explanatum.co.uk)
// - Sends Authorization: Bearer <userId> per platform convention
// - Centralized error handling

import { SocialMediaPlannerService } from './socialMediaPlannerService';

// Thin AI-facing wrapper around SocialMediaPlannerService to avoid duplication and
// to reuse existing auth, endpoints, validation, and error handling.

export interface LinkedInAudience {
  visibility?: "PUBLIC" | "CONNECTIONS";
}

export interface LinkedInPostItem {
  content: string;
  when: "now" | string; // ISO datetime string when scheduling
  audience?: LinkedInAudience; // currently unused by planner endpoints
  accountId?: string; // currently unused by planner endpoints
  media_urls?: string[];
  image_alt?: string;
}

function assertString(name: string, val: any) {
  if (typeof val !== "string" || !val.trim()) throw new Error(`${name} is required`);
}

export const socialSchedulerService = {
  async postLinkedInNow(item: LinkedInPostItem) {
    assertString("content", item.content);
    const planner = new SocialMediaPlannerService();
    // Delegate to publishToLinkedInNow; supports optional single image via media fields
    return await planner.publishToLinkedInNow(item.content, {
      media_urls: item.media_urls,
      image_alt: item.image_alt,
    });
  },

  async scheduleLinkedInPost(item: LinkedInPostItem) {
    assertString("content", item.content);
    const planner = new SocialMediaPlannerService();
    // If 'now', publish immediately via publish endpoint instead of scheduler
    if (item.when === "now") {
      return await planner.publishToLinkedInNow(item.content, {
        media_urls: item.media_urls,
        image_alt: item.image_alt,
      });
    }
    // Try to normalize human-friendly inputs to ISO; final validation happens in planner service
    let scheduledForISO = String(item.when);
    const parsed = Date.parse(scheduledForISO);
    if (!Number.isNaN(parsed)) {
      scheduledForISO = new Date(parsed).toISOString();
    }
    return await planner.scheduleLinkedInPost(item.content, scheduledForISO, {
      media_urls: item.media_urls,
      image_alt: item.image_alt,
    });
  },

  async scheduleMultipleLinkedInPosts(items: LinkedInPostItem[]) {
    if (!Array.isArray(items) || items.length === 0) throw new Error("At least one post is required");
    items.forEach((it) => { assertString("content", it.content); });
    const planner = new SocialMediaPlannerService();
    // Execute sequentially to surface per-item errors clearly
    const results: any[] = [];
    for (const it of items) {
      // If "now", use publish endpoint for immediate post; otherwise schedule
      if (it.when === "now") {
        results.push(await planner.publishToLinkedInNow(it.content, {
          media_urls: it.media_urls,
          image_alt: it.image_alt,
        }));
      } else {
        let scheduledForISO = String(it.when);
        const p = Date.parse(scheduledForISO);
        if (!Number.isNaN(p)) {
          scheduledForISO = new Date(p).toISOString();
        }
        results.push(await planner.scheduleLinkedInPost(it.content, scheduledForISO, {
          media_urls: it.media_urls,
          image_alt: it.image_alt,
        }));
      }
    }
    return { results };
  }
};

export type SocialSchedulerService = typeof socialSchedulerService;
